<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { WeekProgress } from '@/utils/homebase';

// The running week against its Sollstunden (#403) — a progress bar, the seven
// days underneath it, and the person's working-time account balance AS OF the
// last completed week.
//
// WHAT THE BAR FILLS AGAINST is the WHOLE week's target, not the part of it that
// has already come due. On Monday morning that reads 0 of 40, which is the plain
// meaning of "hours this week". There is deliberately no "due by now" mark and no
// behind-sentence: nobody is short for a day that has not ended. The one
// statement about being ahead or behind is the account balance, and that is only
// ever stated for a point in time that is over (`balanceAsOf`).
//
// WITHOUT A TARGET there is no bar at all — no grey track, no 0%. time-api
// refuses to invent eight hours a day for someone with neither a work-time model
// nor contract hours, and drawing an empty bar would put that invented number
// back on the screen by implication. The day strip still renders: the hours are
// true whether or not anybody agreed a target for them.

const props = withDefaults(defineProps<{ progress: WeekProgress; flat?: boolean }>(), { flat: false });

const { t, locale } = useI18n();

const trustBased = computed(() => props.progress.trustBased === true);
// Her contract frees her from booking working time (issue 981): no target, no
// bar, no balance, no working-time days -- her week is the hours per project.
const projectOnly = computed(() => props.progress.projectOnly === true);
const PROJECT_LIST_CAP = 5;
const shownProjects = computed(() => (props.progress.projects ?? []).slice(0, PROJECT_LIST_CAP));
const moreProjects = computed(() => Math.max(0, (props.progress.projects ?? []).length - PROJECT_LIST_CAP));
const hasTarget = computed(() => (props.progress.targetMinutes ?? 0) > 0);

const hours = (minutes: number) => Math.round((minutes / 60) * 10) / 10;
const formatHours = (minutes: number) =>
  new Intl.NumberFormat(locale.value, { maximumFractionDigits: 1 }).format(hours(minutes));

const percent = computed(() => {
  const target = props.progress.targetMinutes ?? 0;
  if (target <= 0) return 0;
  // Capped for the WIDTH only. Overtime is real and the numbers above the bar
  // say so; a bar running past its own track just breaks the layout.
  return Math.min(100, Math.round((props.progress.workedMinutes / target) * 100));
});

// The account balance as of the last completed week, e.g. "+3:20". Signed so a
// balance of exactly zero reads "+0:00" — the same convention time-ui uses.
const formatSigned = (minutes: number) => {
  const rounded = Math.round(minutes);
  const abs = Math.abs(rounded);
  return `${rounded < 0 ? '-' : '+'}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`;
};

// The as-of day as a short weekday and day/month, e.g. "So 19.10.".
const formatAsOfDate = (iso: string, locale: string): string => {
  const parts = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  }).formatToParts(new Date(`${iso}T00:00:00.000Z`));
  // "So 19.10.", not "So., 19.10.": the weekday loses its abbreviation period and
  // the separator after it becomes one plain space; every other part stays as Intl wrote it.
  return parts
    .map((part, i) => {
      if (part.type === 'weekday') return part.value.replace(/\.$/, '');
      if (part.type === 'literal' && parts[i - 1]?.type === 'weekday') return ' ';
      return part.value;
    })
    .join('');
};

// Null when she has no work-time model or no completed week yet: the figure and
// its sentence are then absent, never a 0:00 or a placeholder.
const balance = computed(() => {
  const asOf = props.progress.balanceAsOf;
  if (!asOf) return null;
  return {
    // The date is a UTC calendar day — formatted as UTC so it never slips a day.
    date: formatAsOfDate(asOf.date, locale.value),
    value: formatSigned(asOf.balanceMinutes),
  };
});

// The tallest thing the strip has to draw. Taken over targets AND worked
// minutes so an overtime day still fits inside its own column instead of
// clipping at the target line.
const dayScale = computed(() =>
  Math.max(
    60,
    ...props.progress.days.map((day) => Math.max(day.workedMinutes, day.targetMinutes ?? 0)),
  ),
);

const days = computed(() =>
  props.progress.days.map((day) => {
    const date = new Date(`${day.date}T00:00:00.000Z`);
    return {
      ...day,
      // UTC, like the date keys themselves: rendering the weekday host-local
      // would name the day before for anyone west of UTC.
      weekday: date.toLocaleDateString(locale.value, { weekday: 'short', timeZone: 'UTC' }),
      full: date.toLocaleDateString(locale.value, { dateStyle: 'medium', timeZone: 'UTC' }),
      workedPercent: Math.min(100, Math.round((day.workedMinutes / dayScale.value) * 100)),
      targetPercent:
        day.targetMinutes == null
          ? null
          : Math.min(100, Math.round((day.targetMinutes / dayScale.value) * 100)),
    };
  }),
);
</script>

<template>
  <section class="week" :class="{ 'week--flat': flat }" data-testid="week-progress">
    <!-- Project time only (issue 981): her week is what she booked on projects -- the
         list when there is one, the contract sentence alone when there is not. -->
    <template v-if="projectOnly">
      <div class="week-head">
        <span class="week-title">{{ t('homebase.week.title') }}</span>
      </div>
      <ul v-if="shownProjects.length" class="week-projects" role="list" data-testid="week-projects">
        <li v-for="project in shownProjects" :key="project.projectId" class="week-project">
          <span class="week-project-name">{{ project.name }}</span>
          <span class="week-project-hours">{{ formatHours(project.minutes) }} {{ t('homebase.week.hoursUnit') }}</span>
        </li>
        <li v-if="moreProjects" class="week-project week-project--more" data-testid="week-projects-more">
          {{ t('homebase.week.projectsMore', { n: moreProjects }) }}
        </li>
      </ul>
      <p v-else class="week-note" data-testid="week-project-only">{{ t('homebase.workTimeFree') }}</p>
    </template>
    <template v-else>
      <div class="week-head">
        <span class="week-title">{{ t('homebase.week.title') }}</span>
        <span class="week-figures">
          <strong>{{ formatHours(progress.workedMinutes) }}</strong>
          <template v-if="hasTarget">
            <span class="week-of">/ {{ formatHours(progress.targetMinutes ?? 0) }}</span>
          </template>
          {{ t('homebase.week.hoursUnit') }}
        </span>
      </div>

      <div
        v-if="hasTarget"
        class="week-track"
        role="progressbar"
        :aria-valuenow="percent"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="t('homebase.week.title')"
      >
        <div class="week-fill" :style="{ width: `${percent}%` }" />
      </div>

      <!-- Vertrauensarbeitszeit (issue 981): she records, but no Soll is held against her.
           One sentence says so where the balance would sit — no figure, no target. -->
      <p v-if="trustBased" class="week-note" data-testid="week-trust-based">{{ t('homebase.week.trustBased') }}</p>
      <div v-else-if="balance" class="week-balance" data-testid="week-balance">
        <p class="week-balance-figure">{{ t('homebase.week.balance', { date: balance.date, balance: balance.value }) }}</p>
        <p class="week-note">{{ t('homebase.week.balanceHint') }}</p>
      </div>
      <p v-if="!hasTarget && !trustBased" class="week-note">{{ t('homebase.week.noTarget') }}</p>

      <!-- The seven days. Every one of them is drawn, weekends and empty days
           included: a strip that omits them draws a shorter week. -->
      <ul class="week-days" role="list" :aria-label="t('homebase.week.days')">
        <li
          v-for="day in days"
          :key="day.date"
          class="week-day"
          :class="{ 'week-day--today': day.today }"
          :title="`${day.full}: ${formatHours(day.workedMinutes)} ${t('homebase.week.hoursUnit')}`"
        >
          <span class="week-day-column">
            <!-- The target line sits behind the bar, so a short day reads as a bar
                 that stops below it rather than as a bar with nothing to compare
                 against. A day that owes nothing (weekend, holiday, approved
                 absence) simply has no line. -->
            <span
              v-if="day.targetPercent"
              class="week-day-target"
              :style="{ bottom: `${day.targetPercent}%` }"
              aria-hidden="true"
            />
            <span class="week-day-bar" :style="{ height: `${day.workedPercent}%` }" aria-hidden="true" />
          </span>
          <span class="week-day-label">{{ day.weekday }}</span>
        </li>
      </ul>
    </template>
  </section>
</template>

<style scoped>
/* The whole strip sits inside a wash tile (spec note-ws-brief): one step below
   the white card it lives in. */
.week { display: flex; flex-direction: column; gap: 8px; background: var(--surface-wash); border-radius: var(--radius-md, 12px); padding: 14px 16px; }
/* Embedded directly in a module band (workspace-start M2): the band itself is
   the white surface, so the strip loses its own wash box and padding rather
   than nesting one wash tone inside another. */
.week--flat { background: transparent; padding: 0; }
.week-head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.week-title {
  font-size: var(--text-caps);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}
.week-figures { font-size: var(--text-sm); color: var(--text-secondary); }
.week-figures strong { font-size: var(--text-base); color: var(--text-heading); }
.week-of { color: var(--text-tertiary); }

.week-track {
  position: relative;
  height: 10px;
  border-radius: 999px;
  background: var(--surface-card);
  overflow: hidden;
}
/* The running week's own fill on the single-series lavender scale (spec §2
   data colors) — the same scale the day columns below and the module's
   breakdown bar use. */
.week-fill {
  height: 100%;
  border-radius: 999px;
  background: var(--scale-4);
  transition: width 400ms ease;
}
.week-balance { display: flex; flex-direction: column; gap: 2px; }
.week-balance-figure { margin: 0; font-size: var(--text-sm); color: var(--text-secondary); }

.week-note { margin: 0; font-size: var(--text-xs); color: var(--text-tertiary); }

.week-projects { display: flex; flex-direction: column; gap: 4px; margin: 0; padding: 0; list-style: none; }
.week-project { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; font-size: var(--text-sm); color: var(--text-secondary); }
.week-project-name { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--text-heading); }
.week-project-hours { flex-shrink: 0; font-variant-numeric: tabular-nums; }
.week-project--more { font-size: var(--text-xs); color: var(--text-tertiary); }

.week-days {
  display: flex;
  gap: 4px;
  margin: 2px 0 0;
  padding: 0;
  list-style: none;
}
.week-day { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 3px; min-width: 0; }
.week-day-column {
  position: relative;
  display: flex;
  align-items: flex-end;
  width: 100%;
  height: 42px;
  border-radius: 4px;
  background: var(--surface-wash);
  overflow: hidden;
}
/* Weekday bars on the same lavender scale — a quiet step for the rest of the
   week, the deepest (brightest in dark) step for today, so the current day
   keeps reading as the highest step regardless of theme (spec note-ws-dunkel). */
.week-day-bar { width: 100%; border-radius: 4px; background: var(--scale-3); min-height: 2px; }
.week-day--today .week-day-bar { background: var(--scale-5); }
.week-day-target {
  position: absolute;
  left: 0;
  right: 0;
  /* Drawn as a 2px rule sitting ON the target rather than a hairline above it,
     so the day whose target IS the tallest thing in the strip still shows its
     line instead of losing it against the column's top edge. */
  height: 2px;
  margin-bottom: -1px;
  border-radius: 1px;
  background: var(--text-tertiary);
}
.week-day-label { font-size: var(--text-xs); color: var(--text-tertiary); }
.week-day--today .week-day-label { color: var(--text-heading); font-weight: 600; }
</style>
